import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Pencil as EditIcon, Trash2 as DeleteIcon, Play as ExecuteIcon, Workflow as ComposerIcon, ChevronDown as ExpandMoreIcon } from 'lucide-react';
import { Chip } from '@coordio/ui';
import { workflowService } from '../services/workflowService';
import { useNotificationStore } from '../store/notificationStore';
import { FormSection, InlineErrorPanel, LoadingSkeleton } from '@coordio/ui/layout';
import { PageDetailLayout } from '../components/layouts/PageDetailLayout';
import { useConfirmDialog } from '../components/common/ConfirmDialog';
import type { WorkflowDetailDto, WorkflowExecutionSummaryDto } from '../services/workflowService';
import { ROUTES } from '../utils/routes';
import { ExecuteWorkflowDialog } from '../components/workflows/ExecuteWorkflowDialog';
import { workflowRequiresInput } from '../utils/workflowInput';

const IN_FLIGHT = new Set(['Pending', 'Running', 'Paused']);

const statusColor = (status: string): 'green' | 'blue' | 'amber' | 'red' | 'default' => {
  switch (status) {
    case 'Completed':
      return 'green';
    case 'Running':
    case 'Pending':
      return 'blue';
    case 'Paused':
      return 'amber';
    case 'Failed':
      return 'red';
    default:
      return 'default';
  }
};

/**
 * Workflow detail page component.
 */
export const WorkflowDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addNotification } = useNotificationStore();
  const confirm = useConfirmDialog();

  const [workflow, setWorkflow] = useState<WorkflowDetailDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [executing, setExecuting] = useState(false);
  const [executeDialogOpen, setExecuteDialogOpen] = useState(false);
  const [executions, setExecutions] = useState<WorkflowExecutionSummaryDto[]>([]);
  const [executionsLoading, setExecutionsLoading] = useState(false);
  const [executionsError, setExecutionsError] = useState<string | null>(null);
  const [expandedExecutionId, setExpandedExecutionId] = useState<string | null>(null);
  const [selectedExecution, setSelectedExecution] = useState<WorkflowExecutionSummaryDto | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadExecutions = useCallback(async () => {
    if (!id) return;
    setExecutionsLoading(true);
    setExecutionsError(null);
    try {
      const result = await workflowService.getExecutions(id, 50);
      setExecutions(result);
    } catch (err: any) {
      setExecutionsError(err.response?.data?.message || 'Failed to load executions');
    } finally {
      setExecutionsLoading(false);
    }
  }, [id]);

  const loadWorkflow = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const result = await workflowService.getWorkflow(id);
      setWorkflow(result);
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to load workflow';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  }, [id, addNotification]);

  useEffect(() => {
    if (id) {
      void loadWorkflow();
      void loadExecutions();
    }
  }, [id, loadWorkflow, loadExecutions]);

  useEffect(() => {
    if (!id) return;
    const hasInFlight = executions.some((e) => IN_FLIGHT.has(e.status));
    if (!hasInFlight) return;

    const timer = window.setInterval(() => {
      void loadExecutions();
    }, 4000);
    return () => window.clearInterval(timer);
  }, [id, executions, loadExecutions]);

  const handleDelete = async () => {
    if (!id) return;
    const ok = await confirm({
      title: 'Delete workflow',
      message: `Are you sure you want to delete "${workflow?.name}"?`,
      confirmText: 'Delete',
    });
    if (!ok) return;
    try {
      await workflowService.deleteWorkflow(id);
      addNotification('Workflow deleted successfully', 'success');
      navigate('/workflows');
    } catch (err: any) {
      addNotification(err.response?.data?.message || 'Failed to delete workflow', 'error');
    }
  };

  const handleExecuteConfirm = async (input: string) => {
    if (!id) return;
    setExecuting(true);
    try {
      const trimmed = input.trim();
      const response = await workflowService.executeWorkflow(
        id,
        trimmed ? { parameters: { input: trimmed } } : undefined,
      );
      addNotification(`Workflow execution started: ${response.executionId}`, 'success');
      setExecuteDialogOpen(false);
      await loadExecutions();
    } catch (err: any) {
      addNotification(err.response?.data?.message || 'Failed to execute workflow', 'error');
    } finally {
      setExecuting(false);
    }
  };

  const handleSelectExecution = async (executionId: string) => {
    setDetailLoading(true);
    try {
      const detail = await workflowService.getExecution(executionId);
      setSelectedExecution(detail);
    } catch (err: any) {
      addNotification(err.response?.data?.message || 'Failed to load execution detail', 'error');
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <>
    <PageDetailLayout
      data-testid="workflow-form-page"
      title={workflow?.name || 'Workflow'}
      subtitle={workflow?.description}
      breadcrumbs={[{ label: 'Workflows', href: '/workflows' }, { label: workflow?.name || 'Workflow' }]}
      backTo={{ label: 'Workflows', href: '/workflows' }}
      loading={loading}
      error={error || undefined}
      onErrorDismiss={() => setError(null)}
      status={workflow ? { label: workflow.isActive ? 'Active' : 'Inactive', color: workflow.isActive ? 'success' : 'default' } : undefined}
      chips={workflow ? [
        { label: workflow.workflowType, variant: 'outlined' as const },
        ...(workflow.patternType !== 'None' ? [{ label: workflow.patternType, variant: 'outlined' as const }] : []),
        { label: `v${workflow.version}`, variant: 'outlined' as const },
        ...(workflow.supportsCheckpointing ? [{ label: 'Checkpointing', variant: 'outlined' as const }] : []),
        ...(workflow.supportsStreaming ? [{ label: 'Streaming', variant: 'outlined' as const }] : []),
      ] : []}
      actions={workflow ? [
        {
          icon: executing
            ? <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
            : <ExecuteIcon />,
          label: 'Execute',
          onClick: () => setExecuteDialogOpen(true),
          variant: 'contained' as const,
          disabled: executing || !workflow.isActive,
        },
        {
          icon: <ComposerIcon />,
          label: 'Open in Composer',
          onClick: () => navigate(ROUTES.COMPOSER_DETAIL.replace(':id', id!)),
          variant: 'outlined' as const,
        },
        { icon: <EditIcon />, label: 'Edit', onClick: () => navigate(`/workflows/${id}/edit`) },
        { icon: <DeleteIcon />, label: 'Delete', onClick: handleDelete, color: 'error' as const },
      ] : []}
      tabs={workflow ? [
        {
          label: 'Definition',
          value: 'definition',
          content: (
            <FormSection data-testid="workflow-section-definition" title="Workflow JSON Definition">
              <pre
                className="m-0 p-3 font-mono text-sm overflow-auto max-h-[600px] border border-[var(--color-border-default)] rounded bg-[var(--color-bg-subtle)]"
              >
                {JSON.stringify(JSON.parse(workflow.workflowJson), null, 2)}
              </pre>
            </FormSection>
          ),
        },
        {
          label: 'Executions',
          value: 'executions',
          content: (
            <FormSection data-testid="workflow-section-executions" title="Recent Executions">
              {executionsError && (
                <InlineErrorPanel message={executionsError} />
              )}

              {executionsLoading && executions.length === 0 ? (
                <LoadingSkeleton rows={3} />
              ) : executions.length === 0 ? (
                <p className="text-sm text-[var(--color-text-secondary)]">
                  No executions yet. Use the Execute button to start a new execution.
                </p>
              ) : (
                <div className="flex flex-col gap-3">
                  {executions.map((exec) => {
                    const startedLabel = exec.startedAt
                      ? new Date(exec.startedAt).toLocaleString()
                      : new Date(exec.createdAt).toLocaleString();
                    const completedLabel = exec.completedAt
                      ? new Date(exec.completedAt).toLocaleString()
                      : null;
                    const isExpanded = expandedExecutionId === exec.id;

                    return (
                      <div
                        key={exec.id}
                        className="overflow-hidden rounded-lg border border-[var(--color-border-default)] bg-[var(--color-bg-subtle)]"
                      >
                        <button
                          type="button"
                          className="flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-[var(--color-bg-inset)]"
                          onClick={() => {
                            if (isExpanded) {
                              setExpandedExecutionId(null);
                              setSelectedExecution(null);
                            } else {
                              setExpandedExecutionId(exec.id);
                              void handleSelectExecution(exec.id);
                            }
                          }}
                        >
                          <Chip
                            label={exec.status}
                            color={statusColor(exec.status)}
                            className="shrink-0"
                          />
                          <div className="min-w-0 flex-1">
                            <p
                              className="truncate font-mono text-sm text-[var(--color-text-primary)]"
                              title={exec.id}
                            >
                              {exec.id}
                            </p>
                            <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-[var(--color-text-secondary)]">
                              <span>{startedLabel}</span>
                              {completedLabel && <span>Done: {completedLabel}</span>}
                              {exec.currentNodeId && <span>Node: {exec.currentNodeId}</span>}
                            </div>
                          </div>
                          <ExpandMoreIcon
                            className={`h-4 w-4 shrink-0 text-[var(--color-text-secondary)] transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                          />
                        </button>
                        {isExpanded && (
                          <div className="border-t border-[var(--color-border-default)] bg-[var(--color-bg-surface)] px-3 py-3">
                            {detailLoading && selectedExecution?.id === exec.id ? (
                              <LoadingSkeleton rows={2} />
                            ) : (
                              <div className="flex flex-col gap-3">
                                {exec.errorMessage && (
                                  <InlineErrorPanel message={exec.errorMessage} />
                                )}
                                <div>
                                  <p className="mb-1.5 text-xs font-medium text-[var(--color-text-secondary)]">
                                    Execution state
                                  </p>
                                  <pre className="m-0 max-h-[320px] overflow-auto rounded border border-[var(--color-border-default)] bg-[var(--color-bg-subtle)] p-2 font-mono text-[0.8rem]">
                                    {selectedExecution?.id === exec.id && selectedExecution.stateJson
                                      ? (() => {
                                          try {
                                            return JSON.stringify(JSON.parse(selectedExecution.stateJson), null, 2);
                                          } catch {
                                            return selectedExecution.stateJson;
                                          }
                                        })()
                                      : 'Expand to load state (output_*, session_*, sessionId_*).'}
                                  </pre>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </FormSection>
          ),
        },
      ] : []}
    />
    <ExecuteWorkflowDialog
      open={executeDialogOpen}
      workflowName={workflow?.name || 'Workflow'}
      requiresInput={workflowRequiresInput(workflow?.workflowJson)}
      loading={executing}
      onClose={() => setExecuteDialogOpen(false)}
      onConfirm={(input) => void handleExecuteConfirm(input)}
    />
    </>
  );
};
